/**
 * The wire shapes `Rws.AiInsights.Host.Settings.PortalSettingsAdminEndpoints` returns for the Features tab of System Settings: the
 * features the portal has (defined in code), whether each is on, and the roles it applies to. Gated on `settings.write`. Field names
 * and casing follow `FeatureFlagResponse` and `FeatureFlagRoleResponse` in `Host/Settings/Models/`.
 */
import { adminRequest, type AdminResult } from "./adminApi";

/** A role a feature applies to. */
export interface FeatureFlagRole {
  id: string;
  name: string;
}

/** One feature. It applies to a person only when it is on and the person holds one of its roles. */
export interface FeatureFlag {
  key: string;
  description: string;
  enabled: boolean;
  roles: FeatureFlagRole[];
  updatedAt: string;
  /** The administrator who last changed it, or null while it is still as first seeded. */
  updatedByPersonId: string | null;
  updatedByName: string | null;
  /** When an administrator first saved the feature, or null while it is still as seeded (it then applies to nobody). */
  configuredAt?: string | null;
}

const featuresPath = "/api/entitlement/settings/features";

/** Calls `GET /api/entitlement/settings/features`. */
export async function fetchFeatureFlags(): Promise<AdminResult<FeatureFlag[]>> {
  return adminRequest<FeatureFlag[]>(featuresPath, { method: "GET" }, "features");
}

/** Calls `PUT /api/entitlement/settings/features/{key}`, replacing the feature's on/off state and its whole set of roles. */
export async function updateFeatureFlag(key: string, enabled: boolean, roleIds: string[]): Promise<AdminResult<FeatureFlag>> {
  return adminRequest<FeatureFlag>(`${featuresPath}/${encodeURIComponent(key)}`, { method: "PUT", body: JSON.stringify({ enabled, roleIds }) }, "feature update");
}
